/** Геометрия окна листа РД: рамки строки и шапки на картинке листа, масштаб и
 *  прокрутка к строке.
 *
 *  Рамки (`raw.box`, `raw.head_box`) — в пунктах листа без поворота; картинку
 *  doc-processor рисует с поворотом /Rotate. Геометрия листа приходит в
 *  заголовке `X-Osnova-Sheet`: размер листа в пунктах (как картинка), матрица
 *  поворота `[a, b, c, d, e, f]` (x' = a·x + c·y + e, y' = b·x + d·y + f) и
 *  размер картинки в пикселях.
 *
 *  Без импортов значений: модуль проверяет `node --test` (`rdSheet/logic.test.mjs`). */
import type { Rect } from './protocol';

/** Геометрия листа (заголовок `X-Osnova-Sheet`). */
export interface SheetMeta {
  page: number;
  pages: number;
  /** Размер листа в пунктах с учётом поворота — как картинка. */
  width: number;
  height: number;
  rotation: number;
  matrix: [number, number, number, number, number, number];
  dpi: number;
  pxWidth: number;
  pxHeight: number;
}

/** Прямоугольник на картинке, пиксели картинки. */
export interface PxRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function finite(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Заголовок `X-Osnova-Sheet` → геометрия; нет или битый — null (лист
 *  показывается без рамок). */
export function parseSheetMeta(raw: string | null | undefined): SheetMeta | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (v === null || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const m = o.matrix;
  if (!Array.isArray(m) || m.length !== 6 || !m.every(finite)) return null;
  const nums = [o.page, o.pages, o.width, o.height, o.px_width, o.px_height];
  if (!nums.every(finite)) return null;
  const meta: SheetMeta = {
    page: o.page as number,
    pages: o.pages as number,
    width: o.width as number,
    height: o.height as number,
    rotation: finite(o.rotation) ? o.rotation : 0,
    matrix: m as SheetMeta['matrix'],
    dpi: finite(o.dpi) ? o.dpi : 0,
    pxWidth: o.px_width as number,
    pxHeight: o.px_height as number,
  };
  return meta.width > 0 && meta.height > 0 && meta.pxWidth > 0 && meta.pxHeight > 0 ? meta : null;
}

/** Рамка в пунктах листа без поворота → прямоугольник на картинке (пиксели). */
export function rectToPx(meta: SheetMeta, r: Rect): PxRect {
  const [a, b, c, d, e, f] = meta.matrix;
  const corners: [number, number][] = [
    [r[0], r[1]],
    [r[2], r[1]],
    [r[0], r[3]],
    [r[2], r[3]],
  ];
  const xs = corners.map(([x, y]) => a * x + c * y + e);
  const ys = corners.map(([x, y]) => b * x + d * y + f);
  const kx = meta.pxWidth / meta.width;
  const ky = meta.pxHeight / meta.height;
  const left = Math.min(...xs) * kx;
  const top = Math.min(...ys) * ky;
  return {
    left,
    top,
    width: Math.max(...xs) * kx - left,
    height: Math.max(...ys) * ky - top,
  };
}

/**
 * Высота закреплённой полоски шапки (решение 17) при масштабе 1 — в пикселях
 * картинки листа строки. Пришла картинка листа шапки (`headMeta`) — по ней:
 * разрешение и поворот у неё могут быть свои. Пока не пришла — по геометрии
 * листа строки: продолжение той же таблицы того же файла, формат и поворот
 * листов те же. Так место под полоску резервируется сразу, первая прокрутка к
 * строке уже его учитывает — строка не уходит под шапку, а лист не
 * сдвигается, когда шапка догрузится.
 */
export function stripUnitHeight(
  meta: SheetMeta,
  headBox: Rect,
  headMeta?: SheetMeta | null,
): number {
  const base = headMeta ?? meta;
  // Пиксели картинки шапки → пункты листа → пиксели картинки строки.
  return (
    (rectToPx(base, headBox).height * (meta.pxWidth / meta.width)) / (base.pxWidth / base.width)
  );
}

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 2;

export function clampZoom(z: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/** Масштаб «лист по ширине окна» (CSS-пиксели на пиксель картинки). */
export function fitWidthZoom(meta: Pick<SheetMeta, 'pxWidth'>, viewWidth: number): number {
  return clampZoom((viewWidth - 2) / meta.pxWidth);
}

/** Масштаб «лист целиком». */
export function fitPageZoom(
  meta: Pick<SheetMeta, 'pxWidth' | 'pxHeight'>,
  viewWidth: number,
  viewHeight: number,
): number {
  return clampZoom(Math.min((viewWidth - 2) / meta.pxWidth, (viewHeight - 2) / meta.pxHeight));
}

/** Масштаб под строку: ширина таблицы (рамка шапки или строки) — на 92 %
 *  окна, не мельче «листа по ширине» и не крупнее полуторного. Рамки нет —
 *  «лист по ширине». */
export function rowZoom(meta: SheetMeta, wide: Rect | null, viewWidth: number): number {
  const fit = fitWidthZoom(meta, viewWidth);
  if (!wide) return fit;
  const w = rectToPx(meta, wide).width;
  if (w <= 0) return fit;
  return clampZoom(Math.min(1.5, Math.max(fit, (0.92 * viewWidth) / w)));
}

/** Прокрутка, при которой строка — по центру окна по вертикали, а таблица —
 *  с левого края строки (по горизонтали по центру, если строка уже окна).
 *  `top` — высота закреплённой полоски шапки над листом. */
export function scrollToRect(
  px: PxRect,
  zoom: number,
  view: { width: number; height: number },
  content: { width: number; height: number },
  top = 0,
): { left: number; top: number } {
  const w = px.width * zoom;
  const h = px.height * zoom;
  const visibleH = Math.max(1, view.height - top);
  const left = w < view.width ? px.left * zoom + w / 2 - view.width / 2 : px.left * zoom - 8;
  const y = px.top * zoom + h / 2 - visibleH / 2;
  return {
    left: Math.max(0, Math.min(left, content.width - view.width)),
    top: Math.max(0, Math.min(y, content.height - view.height)),
  };
}

/** Новый масштаб колесом с Ctrl: шаг 10 %, точка под курсором остаётся на
 *  месте — новая прокрутка. */
export function zoomAt(
  zoom: number,
  factor: number,
  anchor: { x: number; y: number },
  scroll: { left: number; top: number },
): { zoom: number; left: number; top: number } {
  const next = clampZoom(zoom * factor);
  const k = next / zoom;
  return {
    zoom: next,
    left: (scroll.left + anchor.x) * k - anchor.x,
    top: (scroll.top + anchor.y) * k - anchor.y,
  };
}
